import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Users, Search, Filter, AlertCircle, Mail, Lock, Unlock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const DAYS = [
  { key: "monday", labelFr: "Lundi", labelEn: "Mon" },
  { key: "tuesday", labelFr: "Mardi", labelEn: "Tue" },
  { key: "wednesday", labelFr: "Mercredi", labelEn: "Wed" },
  { key: "thursday", labelFr: "Jeudi", labelEn: "Thu" },
  { key: "friday", labelFr: "Vendredi", labelEn: "Fri" },
];

// FR grades ordered. Unknown grades fall to the end.
const GRADE_ORDER = ["GS","CP","CE1","CE2","CM1","CM2","6e","5e","4e","3e","2nde","1ere","Tle"];

export default function AdminGrouping() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterGrade, setFilterGrade] = useState("all");
  const [filterTrack, setFilterTrack] = useState("all");
  const [search, setSearch] = useState("");
  const [onlyWithAvailability, setOnlyWithAvailability] = useState(true);

  useEffect(() => {
    api.get("/admin/grouping-helper")
      .then(r => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const toggleUnlock = async (enrollmentId, currentlyUnlocked) => {
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/student-life-unlock`);
      toast.success(r.data.state === "unlocked"
        ? (lang === "fr" ? "Vie scolaire débloquée pour la famille" : "Student life unlocked")
        : (lang === "fr" ? "Vie scolaire reverrouillée" : "Student life re-locked"));
      // Optimistic update
      setItems(prev => prev.map(it => it.enrollment_id === enrollmentId
        ? { ...it, student_life_unlocked_at: r.data.student_life_unlocked_at }
        : it));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const grades = useMemo(() => {
    const s = new Set(items.map(i => i.student?.grade).filter(Boolean));
    return Array.from(s).sort((a, b) => GRADE_ORDER.indexOf(a) - GRADE_ORDER.indexOf(b));
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (filterGrade !== "all" && i.student?.grade !== filterGrade) return false;
      if (filterTrack !== "all" && (i.track || "any") !== filterTrack) return false;
      if (onlyWithAvailability && !i.availability_submitted_at) return false;
      if (search) {
        const s = search.toLowerCase();
        const blob = `${i.student?.first_name||""} ${i.student?.last_name||""} ${i.parent_email||""}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [items, filterGrade, filterTrack, onlyWithAvailability, search]);

  // Group by grade for visual scanning
  const grouped = useMemo(() => {
    const m = new Map();
    for (const it of filtered) {
      const g = it.student?.grade || "—";
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(it);
    }
    // sort by grade order
    return Array.from(m.entries()).sort(([a], [b]) => {
      const ai = GRADE_ORDER.indexOf(a), bi = GRADE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [filtered]);

  const stats = useMemo(() => ({
    total: items.length,
    submitted: items.filter(i => i.availability_submitted_at).length,
    missing: items.filter(i => !i.availability_submitted_at).length,
    unlocked: items.filter(i => i.student_life_unlocked_at).length,
    pendingUnlock: items.filter(i => i.availability_submitted_at && !i.student_life_unlocked_at).length,
    fluent: items.filter(i => i.track === "fluent").length,
    bridge: items.filter(i => i.track === "bridge").length,
  }), [items]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl tracking-tight" data-testid="grouping-title">
          {lang === "fr" ? "Aide au regroupement des élèves" : "Class grouping helper"}
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          {lang === "fr"
            ? "Consultez en un coup d'œil les disponibilités de chaque élève (paiement validé) regroupées par niveau et parcours, pour construire les classes manuellement."
            : "Browse every paid student's submitted availability grouped by grade and track, to assemble classes manually."}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBox label={lang === "fr" ? "Total inscrits payés" : "Paid enrollments"} value={stats.total} icon={<Users className="w-4 h-4" />} />
        <StatBox label={lang === "fr" ? "Dispo renseignées" : "Availability submitted"} value={stats.submitted} tone="success" />
        <StatBox label={lang === "fr" ? "En attente feu vert" : "Awaiting green light"} value={stats.pendingUnlock} tone={stats.pendingUnlock > 0 ? "warning" : "muted"} />
        <StatBox label={lang === "fr" ? "Vie scolaire ouverte" : "Student life unlocked"} value={stats.unlocked} tone="success" />
        <StatBox label={lang === "fr" ? "En attente de dispo" : "Awaiting availability"} value={stats.missing} tone={stats.missing > 0 ? "warning" : "muted"} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold mb-1 block flex items-center gap-1"><Search className="w-3 h-3" />{lang === "fr" ? "Recherche" : "Search"}</label>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={lang === "fr" ? "Nom élève, email parent…" : "Student name, parent email…"} data-testid="grouping-search" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block flex items-center gap-1"><Filter className="w-3 h-3" />{lang === "fr" ? "Niveau" : "Grade"}</label>
            <Select value={filterGrade} onValueChange={setFilterGrade}>
              <SelectTrigger className="w-32" data-testid="grouping-grade"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === "fr" ? "Tous" : "All"}</SelectItem>
                {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">{lang === "fr" ? "Parcours" : "Track"}</label>
            <Select value={filterTrack} onValueChange={setFilterTrack}>
              <SelectTrigger className="w-32" data-testid="grouping-track"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{lang === "fr" ? "Tous" : "All"}</SelectItem>
                <SelectItem value="fluent">Fluent</SelectItem>
                <SelectItem value="bridge">Bridge</SelectItem>
                <SelectItem value="any">{lang === "fr" ? "Indéterminé" : "Undecided"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
            <input type="checkbox" checked={onlyWithAvailability} onChange={e => setOnlyWithAvailability(e.target.checked)} data-testid="grouping-only-submitted" />
            {lang === "fr" ? "Avec dispo renseignées uniquement" : "Only submitted availability"}
          </label>
        </CardContent>
      </Card>

      {loading && <p className="text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</p>}

      {!loading && grouped.length === 0 && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">
          <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {lang === "fr" ? "Aucun élève ne correspond à ces filtres pour le moment." : "No student matches these filters yet."}
        </CardContent></Card>
      )}

      {/* Groups */}
      {grouped.map(([grade, list]) => (
        <Card key={grade} data-testid={`grouping-grade-${grade}`}>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl tracking-tight flex items-center gap-2">
              {lang === "fr" ? `Niveau ${grade}` : `Grade ${grade}`}
              <Badge variant="secondary" className="ml-1">{list.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left p-2 sticky left-0 bg-secondary/60">{lang === "fr" ? "Élève" : "Student"}</th>
                  <th className="text-left p-2">Track</th>
                  {DAYS.map(d => (
                    <th key={d.key} className="text-left p-2 min-w-[140px]">{lang === "fr" ? d.labelFr : d.labelEn}</th>
                  ))}
                  <th className="text-left p-2 min-w-[140px]">{lang === "fr" ? "Notes" : "Notes"}</th>
                  <th className="text-left p-2">{lang === "fr" ? "Parent" : "Parent"}</th>
                  <th className="text-left p-2 min-w-[120px]">SEPA</th>
                  <th className="text-left p-2 min-w-[150px]">{lang === "fr" ? "Vie scolaire" : "Student Life"}</th>
                </tr>
              </thead>
              <tbody>
                {list.map(i => (
                  <tr key={i.enrollment_id} className="border-t hover:bg-secondary/20 transition" data-testid={`grouping-row-${i.enrollment_id}`}>
                    <td className="p-2 font-medium sticky left-0 bg-background">
                      {i.student?.first_name} {i.student?.last_name}
                    </td>
                    <td className="p-2">
                      {i.track === "fluent" && <Badge className="text-[10px] bg-primary/15 text-primary border-primary/30">Fluent</Badge>}
                      {i.track === "bridge" && <Badge className="text-[10px] bg-accent/15 text-accent border-accent/30">Bridge</Badge>}
                      {(i.track === "any" || !i.track) && <Badge variant="outline" className="text-[10px]">—</Badge>}
                    </td>
                    {DAYS.map(d => {
                      const val = (i.availability || {})[d.key] || "";
                      return (
                        <td key={d.key} className={`p-2 align-top text-xs ${val ? "" : "text-muted-foreground/50 italic"}`}>
                          {val || (lang === "fr" ? "—" : "—")}
                        </td>
                      );
                    })}
                    <td className="p-2 align-top text-xs text-muted-foreground italic">
                      {(i.availability || {}).notes || "—"}
                    </td>
                    <td className="p-2">
                      <a href={`mailto:${i.parent_email}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`grouping-email-${i.enrollment_id}`}>
                        <Mail className="w-3 h-3" />
                        {i.parent_email}
                      </a>
                    </td>
                    <td className="p-2">
                      {!i.sepa_required ? (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground" title={lang === "fr" ? "Plan annuel · paiement unique CB" : "Annual plan · single card payment"}>
                          —
                        </Badge>
                      ) : i.sepa_mandate_active ? (
                        <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 gap-1"
                               title={i.sepa_setup_at ? `${lang === "fr" ? "Mandat actif depuis" : "Active since"} ${new Date(i.sepa_setup_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US")}` : ""}
                               data-testid={`grouping-sepa-active-${i.enrollment_id}`}>
                          ✓ {i.sepa_last4 ? `•••• ${i.sepa_last4}` : (lang === "fr" ? "Actif" : "Active")}
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-50 text-amber-800 border-amber-300"
                               title={lang === "fr" ? "Mandat SEPA en attente — relancer la famille" : "SEPA mandate pending — remind the family"}
                               data-testid={`grouping-sepa-missing-${i.enrollment_id}`}>
                          ⚠ {lang === "fr" ? "En attente" : "Pending"}
                        </Badge>
                      )}
                    </td>
                    <td className="p-2">
                      {!i.availability_submitted_at ? (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground">
                          {lang === "fr" ? "Dispo manquante" : "No availability"}
                        </Badge>
                      ) : i.student_life_unlocked_at ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[10px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => toggleUnlock(i.enrollment_id, true)}
                          data-testid={`grouping-relock-${i.enrollment_id}`}
                        >
                          <Unlock className="w-3 h-3" />
                          {lang === "fr" ? "Débloquée" : "Unlocked"}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="h-7 text-[10px] gap-1 bg-primary text-primary-foreground hover:bg-primary/90"
                          onClick={() => toggleUnlock(i.enrollment_id, false)}
                          data-testid={`grouping-unlock-${i.enrollment_id}`}
                        >
                          <Lock className="w-3 h-3" />
                          {lang === "fr" ? "Donner feu vert" : "Give green light"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StatBox({ label, value, icon, tone = "muted" }) {
  const colors = {
    muted: "border-border",
    success: "border-emerald-300 bg-emerald-50",
    warning: "border-amber-300 bg-amber-50",
  };
  return (
    <Card className={colors[tone] || colors.muted}>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">{icon}{label}</div>
        <div className="text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
